'use client';

import {
  AlertTriangle,
  ArrowDownToLine,
  Briefcase,
  Clock,
  Filter,
  HandCoins,
  Info,
  Loader2,
  Receipt,
  Save,
  Search,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CreateCaseDrawer } from '@/components/collections/create-case-drawer';
import { CreatePromiseDrawer, type PromiseFormData } from '@/components/collections/create-promise-drawer';
import { LastActionCell } from '@/components/collections/last-action-cell';
import { ErrorBoundary } from '@/components/error-boundary';
import { HelpSupportButton } from '@/components/help-support';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { CurrencyIcon } from '@/components/shared/currency-icon';
import { type CurrencyMode, CurrencyToggle } from '@/components/shared/currency-toggle';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { AllocationHistoryModal } from '@/components/transactions/allocation-history-modal';
import { FilterSheet } from '@/components/transactions/filter-sheet';
import { SelectionToolbar } from '@/components/transactions/selection-toolbar';
import { SendRemindersDialog } from '@/components/transactions/send-reminders-dialog';
import {
  AddNoteModal,
  CreateDisputeModal,
  type DisputeFormData,
  type NoteFormData,
} from '@/components/transactions/transaction-action-modals';
import { TransactionDetailSheet } from '@/components/transactions/transaction-detail-sheet';
import { TransactionSendCopyInvoiceModal } from '@/components/transactions/transaction-send-copy-invoice-modal';
import { TransactionStatusBadge } from '@/components/transactions/transaction-status-badge';
import { TransactionTypeBadge } from '@/components/transactions/transaction-type-badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useCompany } from '@/contexts/CompanyContext';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useExportAll } from '@/hooks/useExportAll';
import {
  DEFAULT_OUTSTANDING_PRESET_ID,
  filtersMatch,
  OUTSTANDING_TRANSACTIONS_PRESET,
  useFilterPreferences,
} from '@/hooks/useFilterPreferences';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { useSessionSortPreference } from '@/hooks/useSessionSortPreference';
import { useTransactionDetail } from '@/hooks/useTransactionsData';
import {
  buildGraphQLFilterVariable,
  buildMetricFilter,
  mergeFilters,
  sortFieldMap,
  useTransactionsPageDataGraphQL,
} from '@/hooks/useTransactionsGraphQL';
import type { GetTransactionsParams } from '@/lib/api/transactions';
import { calculateTransactionTotals } from '@/lib/export-utils';
import { cn, formatCurrency, formatDateDDMMYYYY } from '@/lib/utils';
import { ExportProvider } from '@/providers/ExportProvider';
import type { ColumnInput } from '@/types/export';
import type { SavedFilterPreset, Transaction, TransactionFilters } from '@/types/transaction';

const DEFAULT_TRANSACTION_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber', label: 'Reference Number', visible: true, order: 0 },
  { id: 'transactionDate', label: 'Transaction Date', visible: true, order: 1 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 2 },
  { id: 'anticipatedDate', label: 'Anticipated Date', visible: true, order: 3 },
  { id: 'customerName', label: 'Customer Name', visible: true, order: 4 },
  { id: 'transactionType', label: 'Type', visible: true, order: 5 },
  { id: 'currency', label: 'Currency', visible: true, order: 6 },
  { id: 'amount', label: 'Gross Amount', visible: true, order: 7 },
  { id: 'outstandingAmount', label: 'Outstanding Amount', visible: true, order: 8 },
  { id: 'status', label: 'Status', visible: true, order: 9 },
  { id: 'description', label: 'Description', visible: true, order: 10 },
  { id: 'createdBy', label: 'Created By', visible: true, order: 11 },
  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 12 },
  { id: 'accountCode', label: 'Account Code', visible: false, order: 13 },
  { id: 'netAmount', label: 'Net Amount', visible: false, order: 14 },
  { id: 'vatAmount', label: 'VAT Amount', visible: false, order: 15 },
  { id: 'subLedger', label: 'Sub Ledger', visible: false, order: 16 },
  { id: 'orderNumber', label: 'Order Number', visible: false, order: 17 },
  { id: 'flags', label: 'Flags', visible: true, order: 18 },
  { id: 'onDispute', label: 'External Dispute', visible: true, order: 19 },
  { id: 'lastAction', label: 'Last Action', visible: true, order: 20 },
  { id: 'actions', label: 'Actions', visible: true, order: 23 },
];

type SortColumn = keyof Transaction | null;
type SortDirection = 'asc' | 'desc';
type MetricFilter = 'outstanding' | 'overdue' | 'at-risk' | 'unallocated-receipts' | null;

const CSV_COLUMN_LABELS: Record<string, string> = {
  referenceNumber: 'Reference Number',
  transactionDate: 'Transaction Date',
  dueDate: 'Due Date',
  anticipatedDate: 'Anticipated Date',
  customerName: 'Customer Name',
  transactionType: 'Type',
  amount: 'Gross Amount',
  currency: 'Currency',
  outstandingAmount: 'Outstanding Amount',
  status: 'Status',
  description: 'Description',
  accountCode: 'Account Code',
  netAmount: 'Net Amount',
  vatAmount: 'VAT Amount',
  onDispute: 'External Dispute',
  subLedger: 'Sub Ledger',
  orderNumber: 'Order Number',
  createdBy: 'Created By',
  modifiedBy: 'Modified By',
  lastAction: 'Last Action',
};

function formatCsvCellValue(key: string, value: unknown, currency: string): string {
  if (key === 'transactionDate' || key === 'dueDate' || key === 'anticipatedDate') {
    return value ? formatDateDDMMYYYY(value as string) : '';
  }
  if (key === 'amount' || key === 'outstandingAmount' || key === 'netAmount' || key === 'vatAmount') {
    return value !== undefined ? formatCurrency(value as number, currency) : '';
  }
  if (key === 'hasDispute') {
    return value ? 'Yes' : 'No';
  }
  if (key === 'lastAction') {
    const lastAction = value as Transaction['lastAction'];
    return lastAction ? lastAction.description || lastAction.type : '';
  }
  if (value === undefined || value === null) {
    return '';
  }
  return String(value);
}

function buildCsvContent(data: Transaction[], visibleColumnKeys: string[]): string {
  const header = visibleColumnKeys.map((key) => `"${CSV_COLUMN_LABELS[key] || key}"`).join(',');
  const rows = data.map((transaction) =>
    visibleColumnKeys
      .map((key) => {
        const value = transaction[key as keyof Transaction];
        const formatted = formatCsvCellValue(key, value, transaction.currency);
        return `"${formatted.replace(/"/g, '""')}"`;
      })
      .join(','),
  );
  return [header, ...rows].join('\n');
}

function downloadCsv(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function getEmptyStateMessage(metricFilter: MetricFilter): string {
  switch (metricFilter) {
    case 'unallocated-receipts':
      return 'No unallocated receipts found';
    case 'outstanding':
      return 'No outstanding invoices found';
    case 'overdue':
      return 'No overdue transactions found';
    case 'at-risk':
      return 'No at-risk invoices found';
    default:
      return 'No transactions found';
  }
}

function getEmptyStateDescription(metricFilter: MetricFilter, searchQuery: string, hasActiveFilters: boolean): string {
  if (metricFilter === 'unallocated-receipts') return 'All receipts have been fully allocated';
  if (searchQuery || hasActiveFilters) return 'Try adjusting your search or filters';
  return 'No transactions match the current criteria';
}

export function TransactionsPage() {
  return (
    <ExportProvider>
      <TransactionsPageInner />
    </ExportProvider>
  );
}

function TransactionsPageInner() {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const baseCurrency = selectedCompany?.currency ?? 'GBP';

  // State
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery);
  const [selectedMetricFilter, setSelectedMetricFilter] = useState<MetricFilter>(null);
  const [filters, setFilters] = useState<TransactionFilters>({ ...OUTSTANDING_TRANSACTIONS_PRESET.filters });
  const { savedPresets, activePresetId, savePreset, deletePreset, applyPreset, clearActivePreset, setActivePresetId } =
    useFilterPreferences<TransactionFilters>(UniqueTableNamesForColumnConfiguration.Transactions, {
      defaultPresets: [OUTSTANDING_TRANSACTIONS_PRESET],
    });

  // Derive effective activePresetId: clear star when filters drift from active preset
  const effectiveActivePresetId = useMemo(() => {
    if (!activePresetId) return null;
    const activePreset = savedPresets.find((x) => x.id === activePresetId);
    if (!activePreset) return null;
    return filtersMatch(activePreset.filters, filters) ? activePresetId : null;
  }, [activePresetId, savedPresets, filters]);
  const {
    columns: txColumns,
    visibleColumns: txVisibleColumns,
    savePreferences: saveTxColumnPreferences,
    resetToDefaults: resetTxColumnPreferences,
    columnOrder,
    setColumnOrder,
    columnWidths,
    setColumnWidths,
    pinnedLeft,
    pinnedRight,
    setPinned,
    resetColumnOrder,
    resetColumnWidths,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.Transactions, DEFAULT_TRANSACTION_COLUMNS);
  const [{ column: sortColumn, direction: sortDirection }, setSortPreference] = useSessionSortPreference<
    SortColumn,
    SortDirection
  >(UniqueTableNamesForColumnConfiguration.Transactions, 'transactionDate', 'desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>('customer');

  // Reset to page 1 when debounced search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const displayAmountSortRemap: Record<string, string> = {
    amount: 'displayAmount',
    outstandingAmount: 'displayOutstandingAmount',
    netAmount: 'displayNetAmount',
    vatAmount: 'displayVatAmount',
  };

  // Build server-side query params from local state
  const serverParams = useMemo((): GetTransactionsParams => {
    const effectiveSortBy = sortColumn
      ? currencyMode === 'customer' && sortColumn in displayAmountSortRemap
        ? displayAmountSortRemap[sortColumn]
        : sortColumn
      : undefined;
    const params: GetTransactionsParams = {
      page: currentPage,
      pageSize,
      sortBy: effectiveSortBy,
      sortOrder: sortDirection,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (selectedMetricFilter) params.metricFilter = selectedMetricFilter;
    if (filters.transactionTypes.length > 0) params.transactionType = filters.transactionTypes.join(',');
    if (filters.statuses.length > 0) params.status = filters.statuses.join(',');
    if (filters.customerIds.length > 0) params.customerId = filters.customerIds.join(',');
    if (filters.currencies && filters.currencies.length > 0) params.currency = filters.currencies.join(',');
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.dueDateFrom) params.dueDateFrom = filters.dueDateFrom;
    if (filters.dueDateTo) params.dueDateTo = filters.dueDateTo;
    if (filters.anticipatedDateFrom) params.anticipatedDateFrom = filters.anticipatedDateFrom;
    if (filters.anticipatedDateTo) params.anticipatedDateTo = filters.anticipatedDateTo;
    if (filters.amountMin !== undefined) params.amountMin = filters.amountMin;
    if (filters.amountMax !== undefined) params.amountMax = filters.amountMax;
    if (filters.outstandingBalanceNotZero) params.outstandingNotZero = true;
    if (filters.outstandingBalanceMin !== undefined) params.outstandingMin = filters.outstandingBalanceMin;
    if (filters.outstandingBalanceMax !== undefined) params.outstandingMax = filters.outstandingBalanceMax;
    if (filters.hasCase) params.hasCase = true;
    if (filters.hasPromise) params.hasPromise = true;
    if (filters.onDisputeFilter) params.onDispute = true;
    if (filters.externalDisputeFilter) params.externalDispute = true;
    return params;
  }, [currentPage, pageSize, sortColumn, sortDirection, debouncedSearch, selectedMetricFilter, filters, currencyMode]);

  // Data from GraphQL hook (handles Demo/Live mode) — server-side pagination
  const {
    transactions,
    pagination,
    totals: dashboardData,
    isLoading,
    refetch,
  } = useTransactionsPageDataGraphQL(serverParams);

  // When exactly one currency is selected in the filter, metric cards should show totals in that currency
  const activeCurrencyFilter =
    filters.currencies && filters.currencies.length === 1 ? filters.currencies[0] : baseCurrency;
  const isSingleCurrencyFiltered = activeCurrencyFilter !== baseCurrency;
  const kpiOutstanding = isSingleCurrencyFiltered
    ? (dashboardData?.totalCurrencyOutstanding ?? 0)
    : (dashboardData?.totalOutstanding ?? 0);
  const kpiOverdue = isSingleCurrencyFiltered
    ? (dashboardData?.overdueCurrencyAmount ?? 0)
    : (dashboardData?.overdueAmount ?? 0);
  const kpiAtRisk = isSingleCurrencyFiltered
    ? (dashboardData?.currencyAtRiskAmount ?? 0)
    : (dashboardData?.atRiskAmount ?? 0);
  const kpiUnallocatedReceipts = isSingleCurrencyFiltered
    ? (dashboardData?.currencyUnallocatedReceiptsAmount ?? 0)
    : (dashboardData?.unallocatedReceiptsAmount ?? 0);

  const totalItems = pagination?.totalItems ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // Sheet/Dialog states
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedGraphQLTransaction, setSelectedGraphQLTransaction] = useState<Transaction | null>(null);
  const { data: transactionDetail } = useTransactionDetail(
    detailSheetOpen ? (selectedGraphQLTransaction?.id ?? null) : null,
  );

  // The REST detail endpoint doesn't return display amounts, so preserve them from the
  // GraphQL-loaded selectedGraphQLTransaction (which does have them).
  const detailTransaction = useMemo(() => {
    if (!transactionDetail) return selectedGraphQLTransaction;
    return {
      ...transactionDetail,
      displayAmount: selectedGraphQLTransaction?.displayAmount,
      displayNetAmount: selectedGraphQLTransaction?.displayNetAmount,
      displayVatAmount: selectedGraphQLTransaction?.displayVatAmount,
      displayOutstandingAmount: selectedGraphQLTransaction?.displayOutstandingAmount,
    };
  }, [transactionDetail, selectedGraphQLTransaction]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null);
  const [savePresetDialogOpen, setSavePresetDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [sendRemindersDialogOpen, setSendRemindersDialogOpen] = useState(false);
  const [sendCopyInvoiceDialogOpen, setSendCopyInvoiceDialogOpen] = useState(false);
  const [createCollectionCaseModalOpen, setCreateCollectionCaseModalOpen] = useState(false);
  const [createPromiseModalOpen, setCreatePromiseModalOpen] = useState(false);
  const [createDisputeModalOpen, setCreateDisputeModalOpen] = useState(false);
  const [addNoteModalOpen, setAddNoteModalOpen] = useState(false);
  const [allocationHistoryModalOpen, setAllocationHistoryModalOpen] = useState(false);

  // Sync activePresetId when filters drift
  useEffect(() => {
    if (activePresetId && effectiveActivePresetId === null) {
      clearActivePreset();
    }
  }, [activePresetId, effectiveActivePresetId, clearActivePreset]);

  // Handlers
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortPreference(column, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortPreference(column, 'asc');
    }
    setCurrentPage(1);
  };

  const handleSelectAll = (checked: boolean | 'indeterminate') => {
    if (checked === true) {
      setSelectedRows(new Set(transactions.map((t) => t.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id: number, checked: boolean | 'indeterminate') => {
    try {
      const newSelected = new Set(selectedRows);
      if (checked === true) {
        newSelected.add(id);
      } else {
        newSelected.delete(id);
      }
      setSelectedRows(newSelected);
    } catch (error) {
      console.error('Error in handleSelectRow:', error);
      toast.error('Failed to select transaction');
    }
  };

  const handleMetricCardClick = (metric: MetricFilter) => {
    // Toggle: if clicking the same metric, deselect it
    if (selectedMetricFilter === metric) {
      setSelectedMetricFilter(null);
      setCurrentPage(1);
      toast.success('Filter cleared');
    } else {
      setSelectedMetricFilter(metric);
      setCurrentPage(1);
      setSelectedRows(new Set()); // Clear selection when changing filters
      const metricNames = {
        outstanding: 'Total Outstanding',
        overdue: 'Overdue',
        'at-risk': 'At Risk Amount',
        'unallocated-receipts': 'Unallocated Receipts',
      };
      const descriptions = {
        outstanding: 'Showing all outstanding/pending invoices',
        overdue: 'Showing transactions past their due date',
        'at-risk': 'Showing invoices overdue or due within 7 days',
        'unallocated-receipts': 'Showing receipts with an outstanding (unallocated) balance',
      };
      toast.success(`${metricNames[metric!]}: ${descriptions[metric!]}`);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilters({ ...OUTSTANDING_TRANSACTIONS_PRESET.filters });
    setSelectedMetricFilter(null);
    setActivePresetId(DEFAULT_OUTSTANDING_PRESET_ID);
    setCurrentPage(1);
    setSelectedRows(new Set());
    toast.success('Filters reset to Outstanding Transactions');
  };

  const handleApplyPreset = (preset: SavedFilterPreset) => {
    setFilters({ ...preset.filters });
    applyPreset(preset);
    setCurrentPage(1);
    setSelectedRows(new Set());
    toast.success(`Filter preset "${preset.name}" applied`);
  };

  const pendingDraftFiltersRef = useRef<TransactionFilters | null>(null);

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      toast.error('Preset name required');
      return;
    }

    const filtersToSave = pendingDraftFiltersRef.current ?? filters;
    try {
      const newPresetId = await savePreset(presetName, filtersToSave);
      setActivePresetId(newPresetId);
      setPresetName('');
      setSavePresetDialogOpen(false);
      pendingDraftFiltersRef.current = null;
      toast.success(`Filter preset "${presetName}" saved`);
    } catch (error) {
      console.error('Failed to save filter preset:', error);
      const message = error instanceof Error ? error.message : 'Failed to save filter preset';
      toast.error(message);
    }
  };

  const handleDeletePreset = async (presetId: string) => {
    const preset = savedPresets.find((x) => x.id === presetId);
    if (preset?.isDefault) return;

    try {
      await deletePreset(presetId);
      toast.success('Filter preset deleted');
    } catch (error) {
      console.error('Failed to delete filter preset:', error);
      toast.error('Failed to delete filter preset');
    }
  };

  const handleViewTransaction = (transaction: Transaction) => {
    setSelectedGraphQLTransaction(transaction);
    setDetailSheetOpen(true);
  };

  // handleDeleteTransaction - available for future use when delete API is implemented
  void ((transaction: Transaction) => {
    setTransactionToDelete(transaction);
    setDeleteDialogOpen(true);
  });

  const confirmDelete = () => {
    if (transactionToDelete) {
      toast.success('Transaction deleted');
      setTransactionToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  // handleBulkDelete - available for future use when bulk delete API is implemented
  void (() => {
    toast.success(`${selectedRows.size} transactions deleted`);
    setSelectedRows(new Set());
  });

  // Map frontend column IDs to TransactionListSort enum names for the export API.
  // Extends the shared sortFieldMap with columns that are valid for export
  // but not used for sorting on the list query.
  // When currencyMode is "customer", amount columns remap to Display* variants;
  // otherwise they keep the default base-currency names from sortFieldMap.
  const exportColumnNameMap: Record<string, string> = useMemo(() => {
    const base: Record<string, string> = {
      ...sortFieldMap,
      accountCode: 'AccountCode',
      netAmount: 'NetAmount',
      vatAmount: 'VatAmount',
      hasDispute: 'HasDispute',
      flags: 'Flags',
      subLedger: 'SubLedger',
      orderNumber: 'OrderNumber',
    };
    if (currencyMode === 'customer') {
      base.amount = 'DisplayAmount';
      base.outstandingAmount = 'DisplayOutstandingAmount';
      base.netAmount = 'DisplayNetAmount';
      base.vatAmount = 'DisplayVatAmount';
    }
    return base;
  }, [currencyMode]);

  // Build columns for server-side export based on visible columns
  const getExportColumns = useCallback((): ColumnInput[] => {
    return txColumns
      .filter((col) => col.visible && col.id !== 'actions' && col.id in exportColumnNameMap)
      .map((col) => ({
        name: exportColumnNameMap[col.id],
        displayName: col.label,
      }));
  }, [txColumns, exportColumnNameMap]);

  // Build columns for server-side export with all columns
  const getAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_TRANSACTION_COLUMNS.filter((col) => col.id !== 'actions' && col.id in exportColumnNameMap).map(
      (col) => ({
        name: exportColumnNameMap[col.id],
        displayName: col.label,
      }),
    );
  }, [exportColumnNameMap]);

  // Reuse the same filter builder used by the GraphQL queries
  const getExportFilter = useCallback((): Record<string, unknown> | undefined => {
    const regularFilter = buildGraphQLFilterVariable(serverParams);
    const metricFilterObj = buildMetricFilter(serverParams.metricFilter);
    return mergeFilters(regularFilter, metricFilterObj);
  }, [serverParams]);

  // Server-side export hook for current view columns
  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: 'Transactions',
    getColumns: getExportColumns,
    getFilter: getExportFilter,
    fileName: `transactions-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Server-side export hook for all columns
  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: 'Transactions',
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    fileName: `transactions-all-columns-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Handle export to Excel (server-side)
  const handleExportCurrentView = () => {
    triggerExportCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  // Handle export all columns to Excel (server-side)
  const handleExportAllColumns = () => {
    triggerExportAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleSendReminders = (transactionIds: number[]) => {
    // In a real app, this would call an API
    toast.success(`Payment reminders sent for ${transactionIds.length} invoice(s)`);
  };

  const handleSendCopyInvoiceSuccess = () => {
    setSendCopyInvoiceDialogOpen(false);
    setSelectedRows(new Set());
  };

  // Handler for navigating to allocate screen when unallocated receipts are selected
  const handleNavigateToAllocate = () => {
    // Get only unallocated receipts from selection
    const unallocatedReceipts = selectedTransactions.filter(
      (t) => t.transactionType === 'receipt' && t.outstandingAmount !== undefined && t.outstandingAmount !== 0,
    );

    if (unallocatedReceipts.length === 0) {
      toast.error('No unallocated receipts selected');
      return;
    }

    // If only one receipt is selected, navigate directly to its allocate page
    if (unallocatedReceipts.length === 1) {
      navigate(`/payments/allocate/${unallocatedReceipts[0].id}`);
    } else {
      // For multiple receipts, navigate to the first one
      // In a real app, you might want to show a selection dialog or navigate to a list view
      navigate(`/payments/allocate/${unallocatedReceipts[0].id}`);
      toast.info(`Navigating to allocate screen for receipt ${unallocatedReceipts[0].referenceNumber}`);
    }
  };

  const handleCreateCollectionCase = () => {
    // Called when case is created successfully from the drawer
    setCreateCollectionCaseModalOpen(false);
    setSelectedRows(new Set());
  };

  const handleCreatePromise = (_data?: PromiseFormData) => {
    // Called after promise drawer handles the mutation internally
    setCreatePromiseModalOpen(false);
    setSelectedRows(new Set());
  };

  const handleCreateDispute = (_data?: DisputeFormData) => {
    // Called after dispute modal handles the mutation internally
    setCreateDisputeModalOpen(false);
    setSelectedRows(new Set());
  };

  const handleAddNote = (_data?: NoteFormData) => {
    // Called after note modal handles the mutation internally
    setAddNoteModalOpen(false);
    setSelectedRows(new Set());
  };

  const handleViewAllocationHistory = () => {
    setAllocationHistoryModalOpen(true);
  };

  const handleColumnConfigApply = (columns: ColumnDefinition[]) => {
    saveTxColumnPreferences(columns);
  };

  const tableColumns = useMemo((): DataTableColumn[] => {
    const headerMeta: Record<
      string,
      {
        label?: string;
        sortKey?: string;
        align?: 'right';
        defaultWidth?: number;
        className?: string;
        truncate?: boolean | 'whenSized';
      }
    > = {
      referenceNumber: { sortKey: 'referenceNumber', defaultWidth: 160 },
      transactionDate: { sortKey: 'transactionDate', defaultWidth: 140 },
      dueDate: { sortKey: 'dueDate', defaultWidth: 130 },
      anticipatedDate: { sortKey: 'anticipatedDate', defaultWidth: 150 },
      customerName: { sortKey: 'customerName', defaultWidth: 180, truncate: 'whenSized' },
      transactionType: { sortKey: 'transactionType', defaultWidth: 120 },
      currency: { sortKey: 'currency', defaultWidth: 100 },
      amount: {
        label: currencyMode === 'base' ? `Gross (${baseCurrency})` : 'Gross',
        sortKey: 'amount',
        align: 'right',
        defaultWidth: 140,
        className: 'text-right',
      },
      outstandingAmount: {
        label: currencyMode === 'base' ? `Outstanding (${baseCurrency})` : 'Outstanding',
        sortKey: 'outstandingAmount',
        align: 'right',
        defaultWidth: 160,
        className: 'text-right',
      },
      status: { sortKey: 'status', defaultWidth: 120 },
      description: { defaultWidth: 200, truncate: true },
      createdBy: { defaultWidth: 130 },
      modifiedBy: { defaultWidth: 130 },
      accountCode: { sortKey: 'accountCode', defaultWidth: 120 },
      netAmount: { sortKey: 'netAmount', align: 'right', defaultWidth: 120, className: 'text-right' },
      vatAmount: { sortKey: 'vatAmount', align: 'right', defaultWidth: 120, className: 'text-right' },
      subLedger: { sortKey: 'subLedger', defaultWidth: 120 },
      orderNumber: { sortKey: 'orderNumber', defaultWidth: 130 },
      flags: { defaultWidth: 120 },
      onDispute: { sortKey: 'onDispute', defaultWidth: 130 },
      lastAction: { defaultWidth: 180 },
      actions: { defaultWidth: 50 },
    };

    return txVisibleColumns.map((col) => {
      const meta = headerMeta[col.id] || {};
      return {
        id: col.id,
        label: meta.label || col.label,
        sortKey: meta.sortKey,
        align: meta.align,
        defaultWidth: meta.defaultWidth || 150,
        className: meta.className,
        truncate: meta.truncate,
      };
    });
  }, [txVisibleColumns, currencyMode]);

  const renderTransactionCell = useCallback(
    (columnId: string, transaction: Transaction): React.ReactNode => {
      switch (columnId) {
        case 'referenceNumber':
          return (
            <button
              onClick={() => handleViewTransaction(transaction)}
              className="text-primary hover:underline font-medium"
            >
              {transaction.referenceNumber}
            </button>
          );
        case 'transactionDate':
          return formatDateDDMMYYYY(transaction.transactionDate);
        case 'dueDate':
          return transaction.dueDate ? formatDateDDMMYYYY(transaction.dueDate) : '-';
        case 'anticipatedDate':
          return transaction.anticipatedDate ? formatDateDDMMYYYY(transaction.anticipatedDate) : '-';
        case 'customerName':
          return (
            <button
              onClick={() =>
                navigate(`/customers/${transaction.customerId}`, {
                  state: { customer: { name: transaction.customerName, accountNumber: transaction.accountNumber } },
                })
              }
              className="text-primary hover:underline font-medium transition-colors text-left"
              title={`View details for ${transaction.customerName}`}
            >
              {transaction.customerName}
            </button>
          );
        case 'transactionType':
          return <TransactionTypeBadge type={transaction.transactionType} />;
        case 'currency':
          return transaction.currency;
        case 'amount':
          return (
            <span className="font-bold">
              {currencyMode === 'base'
                ? formatCurrency(transaction.amount, baseCurrency)
                : formatCurrency(transaction.displayAmount ?? transaction.amount, transaction.currency)}
            </span>
          );
        case 'outstandingAmount': {
          const outVal =
            currencyMode === 'base'
              ? transaction.outstandingAmount
              : (transaction.displayOutstandingAmount ?? transaction.outstandingAmount);
          return (
            <span className="font-medium">
              {outVal != null
                ? formatCurrency(outVal, currencyMode === 'base' ? baseCurrency : transaction.currency)
                : '-'}
            </span>
          );
        }
        case 'status':
          return transaction.paymentLinkStatus ? (
            <Badge variant="outline" className="capitalize">
              {transaction.paymentLinkStatus.replace(/_/g, ' ')}
            </Badge>
          ) : (
            <TransactionStatusBadge status={transaction.status} />
          );
        case 'description':
          return transaction.description;
        case 'createdBy':
          return transaction.createdBy || '-';
        case 'modifiedBy':
          return transaction.modifiedBy || '-';
        case 'accountCode':
          return transaction.accountCode || '-';
        case 'netAmount': {
          const netVal =
            currencyMode === 'base' ? transaction.netAmount : (transaction.displayNetAmount ?? transaction.netAmount);
          return netVal != null
            ? formatCurrency(netVal, currencyMode === 'base' ? baseCurrency : transaction.currency)
            : '-';
        }
        case 'vatAmount': {
          const vatVal =
            currencyMode === 'base' ? transaction.vatAmount : (transaction.displayVatAmount ?? transaction.vatAmount);
          return vatVal != null
            ? formatCurrency(vatVal, currencyMode === 'base' ? baseCurrency : transaction.currency)
            : '-';
        }
        case 'subLedger':
          return transaction.subLedger || '-';
        case 'orderNumber':
          return transaction.orderNumber || '-';
        case 'flags':
          return (
            <TooltipProvider>
              <div className="flex items-center gap-2">
                {transaction.hasDispute && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 transition-colors cursor-help">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-red-600 text-white border-red-700">
                      <p className="font-semibold">Disputed</p>
                      <p className="text-xs">This transaction has an active dispute</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {transaction.hasPromise && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors cursor-help">
                        <HandCoins className="h-4 w-4 text-blue-600" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-blue-600 text-white border-blue-700">
                      <p className="font-semibold">Promise to Pay</p>
                      <p className="text-xs">Customer has committed to payment</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {transaction.hasCase && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 hover:bg-orange-200 transition-colors cursor-help">
                        <Briefcase className="h-4 w-4 text-orange-600" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-orange-600 text-white border-orange-700">
                      <p className="font-semibold">Collection Case</p>
                      <p className="text-xs">Part of an active collection case</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {!transaction.hasDispute && !transaction.hasPromise && !transaction.hasCase && (
                  <span className="text-muted-foreground text-xs">-</span>
                )}
              </div>
            </TooltipProvider>
          );
        case 'onDispute':
          return transaction.onDispute ? (
            <Badge variant="destructive" className="text-xs">
              Yes
            </Badge>
          ) : (
            <span className="text-muted-foreground">No</span>
          );
        case 'lastAction':
          return <LastActionCell lastAction={transaction.lastAction} />;
        case 'actions':
          return null;
        default:
          return '-';
      }
    },
    [currencyMode, handleViewTransaction, navigate],
  );

  // Get selected transactions
  const selectedTransactions = useMemo(() => {
    try {
      return transactions.filter((t) => selectedRows.has(t.id));
    } catch (error) {
      console.error('Error filtering selected transactions:', error);
      return [];
    }
  }, [transactions, selectedRows]);

  // Calculate totals for selected transactions
  const selectionTotals = useMemo(() => {
    try {
      return calculateTransactionTotals(selectedTransactions, baseCurrency);
    } catch (error) {
      console.error('Error calculating selection totals:', error);
      return {
        count: 0,
        totalAmount: 0,
        totalOutstanding: 0,
        currency: 'GBP',
      };
    }
  }, [selectedTransactions]);

  // Check if there are any non-default filters active
  const hasActiveFilters =
    searchQuery ||
    filters.customerIds.length > 0 ||
    filters.transactionTypes.length > 0 ||
    filters.statuses.length > 0 ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.dueDateFrom ||
    filters.dueDateTo ||
    filters.anticipatedDateFrom ||
    filters.anticipatedDateTo ||
    filters.amountMin !== undefined ||
    filters.amountMax !== undefined ||
    filters.outstandingBalanceMin !== undefined ||
    filters.outstandingBalanceMax !== undefined ||
    filters.hasCase ||
    filters.hasPromise ||
    filters.onDisputeFilter ||
    filters.externalDisputeFilter;

  // Count active filters (excluding the default outstanding balance filter)
  const activeFilterCount =
    (searchQuery ? 1 : 0) +
    (filters.customerIds.length > 0 ? 1 : 0) +
    (filters.transactionTypes.length > 0 ? 1 : 0) +
    (filters.statuses.length > 0 ? 1 : 0) +
    (filters.currencies && filters.currencies.length > 0 ? 1 : 0) +
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0) +
    (filters.dueDateFrom ? 1 : 0) +
    (filters.dueDateTo ? 1 : 0) +
    (filters.anticipatedDateFrom ? 1 : 0) +
    (filters.anticipatedDateTo ? 1 : 0) +
    (filters.amountMin !== undefined ? 1 : 0) +
    (filters.amountMax !== undefined ? 1 : 0) +
    (filters.outstandingBalanceMin !== undefined || filters.outstandingBalanceMax !== undefined ? 1 : 0) +
    (filters.hasCase ? 1 : 0) +
    (filters.hasPromise ? 1 : 0) +
    (filters.onDisputeFilter ? 1 : 0) +
    (filters.externalDisputeFilter ? 1 : 0);

  // Check if default filter is active
  const hasDefaultFilter = filters.outstandingBalanceNotZero === true;

  return (
    <div className="flex flex-col gap-4 p-4 h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Transactions</h1>
          <p className="text-muted-foreground">Comprehensive view of all transaction records</p>
        </div>
        <HelpSupportButton screenId="transactions" />
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card
          className={cn(
            'transition-all border-l-4 border-l-orange-500 cursor-pointer',
            selectedMetricFilter === 'outstanding' ? 'shadow-lg ring-2 ring-offset-1 ring-gray-300' : 'hover:shadow-md',
          )}
          onClick={() => handleMetricCardClick('outstanding')}
          title="Click to filter and show all outstanding/pending invoices"
        >
          <CardContent className="pt-6">
            <div className="relative">
              <div className="absolute top-0 right-0">
                <div className="p-2 rounded-full bg-orange-100 flex-shrink-0">
                  <CurrencyIcon currencyCode={baseCurrency} className="h-4 w-4 text-orange-600" />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <p style={{ fontSize: '15px', fontWeight: 400, color: '#111827' }}>Total Outstanding</p>
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0"
                        aria-label="Total Outstanding info"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px] text-center">
                      {isSingleCurrencyFiltered
                        ? `Total for ${activeCurrencyFilter} transactions only. Values shown in ${activeCurrencyFilter}.`
                        : `Total shown in base currency (${baseCurrency}). Includes all transactions.`}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p style={{ fontSize: '22px', fontWeight: 600, color: '#111827' }} className="mt-1">
                {formatCurrency(kpiOutstanding, activeCurrencyFilter)}
              </p>
              <p style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }} className="mt-1">
                {isSingleCurrencyFiltered
                  ? `Outstanding for ${activeCurrencyFilter} transactions`
                  : 'All outstanding transactions'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card
          className={cn(
            'transition-all border-l-4 border-l-red-500 cursor-pointer',
            selectedMetricFilter === 'overdue' ? 'shadow-lg ring-2 ring-offset-1 ring-gray-300' : 'hover:shadow-md',
          )}
          onClick={() => handleMetricCardClick('overdue')}
          title="Click to filter and show transactions past their due date"
        >
          <CardContent className="pt-6">
            <div className="relative">
              <div className="absolute top-0 right-0">
                <div className="p-2 rounded-full bg-red-100 flex-shrink-0">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <p style={{ fontSize: '15px', fontWeight: 400, color: '#111827' }}>Overdue</p>
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0"
                        aria-label="Overdue info"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px] text-center">
                      {isSingleCurrencyFiltered
                        ? `Overdue total for ${activeCurrencyFilter} transactions only. Values shown in ${activeCurrencyFilter}.`
                        : `Total shown in base currency (${baseCurrency}). Includes all transactions.`}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p style={{ fontSize: '22px', fontWeight: 600, color: '#111827' }} className="mt-1">
                {formatCurrency(kpiOverdue, activeCurrencyFilter)}
              </p>
              <p style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }} className="mt-1">
                {isSingleCurrencyFiltered ? `Overdue for ${activeCurrencyFilter} transactions` : 'Past due date'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card
          className={cn(
            'transition-all border-l-4 border-l-yellow-500 cursor-pointer',
            selectedMetricFilter === 'at-risk' ? 'shadow-lg ring-2 ring-offset-1 ring-gray-300' : 'hover:shadow-md',
          )}
          onClick={() => handleMetricCardClick('at-risk')}
          title="Click to filter and show invoices overdue or due within 7 days"
        >
          <CardContent className="pt-6">
            <div className="relative">
              <div className="absolute top-0 right-0">
                <div className="p-2 rounded-full bg-yellow-100 flex-shrink-0">
                  <Clock className="h-4 w-4 text-yellow-600" />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <p style={{ fontSize: '15px', fontWeight: 400, color: '#111827' }}>At Risk Amount</p>
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0"
                        aria-label="At Risk Amount info"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px] text-center">
                      {isSingleCurrencyFiltered
                        ? `At risk total for ${activeCurrencyFilter} transactions only. Values shown in ${activeCurrencyFilter}.`
                        : `Total shown in base currency (${baseCurrency}). Includes all transactions.`}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p style={{ fontSize: '22px', fontWeight: 600, color: '#111827' }} className="mt-1">
                {formatCurrency(kpiAtRisk, activeCurrencyFilter)}
              </p>
              <p style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }} className="mt-1">
                {isSingleCurrencyFiltered
                  ? `At risk for ${activeCurrencyFilter} transactions`
                  : 'Due within 7 days or overdue'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card
          className={cn(
            'transition-all border-l-4 border-l-purple-500 cursor-pointer',
            selectedMetricFilter === 'unallocated-receipts'
              ? 'shadow-lg ring-2 ring-offset-1 ring-gray-300'
              : 'hover:shadow-md',
          )}
          onClick={() => handleMetricCardClick('unallocated-receipts')}
          title="Click to filter transactions and show only receipts with an outstanding (unallocated) balance"
        >
          <CardContent className="pt-6">
            <div className="relative">
              <div className="absolute top-0 right-0">
                <div className="p-2 rounded-full bg-purple-100 flex-shrink-0">
                  <CurrencyIcon currencyCode={baseCurrency} className="h-4 w-4 text-purple-600" />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <p style={{ fontSize: '15px', fontWeight: 400, color: '#111827' }}>Unallocated Receipts</p>
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0"
                        aria-label="Unallocated Receipts info"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px] text-center">
                      {isSingleCurrencyFiltered
                        ? `Unallocated receipts for ${activeCurrencyFilter} transactions only. Values shown in ${activeCurrencyFilter}.`
                        : `Total shown in base currency (${baseCurrency}). Includes all transactions.`}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p style={{ fontSize: '22px', fontWeight: 600, color: '#111827' }} className="mt-1">
                {formatCurrency(kpiUnallocatedReceipts, activeCurrencyFilter)}
              </p>
              <p style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }} className="mt-1">
                {dashboardData?.unallocatedReceiptsCount ?? 0} receipt
                {(dashboardData?.unallocatedReceiptsCount ?? 0) !== 1 ? 's' : ''} with outstanding balance
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sticky toolbar */}
      <div className="sticky top-0 z-20 bg-background border rounded-t-xl px-4 py-3">
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-xl font-semibold whitespace-nowrap">All Transactions</h2>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by reference, customer, or description..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10"
            />
          </div>
          <CurrencyToggle
            value={currencyMode}
            onChange={setCurrencyMode}
            nonBaseLabel="Transaction Currency"
            baseLabel={`Base Currency (${baseCurrency})`}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFilterSheetOpen(true)}
            className="gap-2 whitespace-nowrap"
          >
            <Filter className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <ArrowDownToLine className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportCurrentView}>Export Current View</DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportAllColumns}>Export All Columns</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ColumnConfigDialog
              columns={txColumns}
              onApply={handleColumnConfigApply}
              onReset={resetTxColumnPreferences}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedMetricFilter && (
            <div
              className={cn(
                'flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border',
                selectedMetricFilter === 'unallocated-receipts' && 'bg-purple-50 border-purple-200',
                selectedMetricFilter === 'outstanding' && 'bg-orange-50 border-orange-200',
                selectedMetricFilter === 'overdue' && 'bg-red-50 border-red-200',
                selectedMetricFilter === 'at-risk' && 'bg-yellow-50 border-yellow-200',
              )}
            >
              <Receipt
                className={cn(
                  'h-3.5 w-3.5',
                  selectedMetricFilter === 'unallocated-receipts' && 'text-purple-600',
                  selectedMetricFilter === 'outstanding' && 'text-orange-600',
                  selectedMetricFilter === 'overdue' && 'text-red-600',
                  selectedMetricFilter === 'at-risk' && 'text-yellow-600',
                )}
              />
              <span
                className={cn(
                  'font-medium',
                  selectedMetricFilter === 'unallocated-receipts' && 'text-purple-700',
                  selectedMetricFilter === 'outstanding' && 'text-orange-700',
                  selectedMetricFilter === 'overdue' && 'text-red-700',
                  selectedMetricFilter === 'at-risk' && 'text-yellow-700',
                )}
              >
                {selectedMetricFilter === 'unallocated-receipts' && 'Unallocated Receipts'}
                {selectedMetricFilter === 'outstanding' && 'Total Outstanding'}
                {selectedMetricFilter === 'overdue' && 'Overdue'}
                {selectedMetricFilter === 'at-risk' && 'At Risk'} filter active
              </span>
              <button
                onClick={() => {
                  setSelectedMetricFilter(null);
                  setCurrentPage(1);
                  toast.success('Metric filter cleared');
                }}
                className={cn(
                  'ml-1 hover:opacity-70 transition-opacity',
                  selectedMetricFilter === 'unallocated-receipts' && 'text-purple-600',
                  selectedMetricFilter === 'outstanding' && 'text-orange-600',
                  selectedMetricFilter === 'overdue' && 'text-red-600',
                  selectedMetricFilter === 'at-risk' && 'text-yellow-600',
                )}
                title="Clear metric filter"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={handleClearFilters} className="gap-2">
              <X className="h-4 w-4" />
              Clear All Filters
            </Button>
          )}
          {hasDefaultFilter && !hasActiveFilters && !selectedMetricFilter && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-blue-50 px-3 py-1.5 rounded-md border border-blue-200">
              <Filter className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-blue-700">
                Default filter active: <span className="font-medium">Outstanding Balance &ne; 0</span>
              </span>
              <button
                onClick={() => {
                  setFilters({
                    ...filters,
                    outstandingBalanceNotZero: undefined,
                  });
                  toast.info('Default filter removed');
                }}
                className="ml-1 text-blue-600 hover:text-blue-800 transition-colors"
                title="Remove default filter"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      <Card className="rounded-t-none border-t-0 flex flex-col flex-1 min-h-0">
        <CardContent className="p-0 flex flex-col flex-1 min-h-0">
          <DataTable
            columns={tableColumns}
            data={transactions}
            getRowId={(t) => t.id}
            renderCell={renderTransactionCell}
            sortField={sortColumn ?? undefined}
            sortDirection={sortDirection}
            onSort={(field) => handleSort(field as SortColumn)}
            showCheckbox
            selectedIds={selectedRows}
            allSelected={transactions.length > 0 && transactions.every((t) => selectedRows.has(t.id))}
            onSelectAll={handleSelectAll}
            onSelectRow={(id, checked) => handleSelectRow(id as number, checked)}
            isLoading={isLoading}
            initialColumnOrder={columnOrder}
            initialColumnWidths={columnWidths}
            initialPinnedLeft={pinnedLeft}
            initialPinnedRight={pinnedRight}
            onColumnOrderChange={setColumnOrder}
            onColumnWidthsChange={setColumnWidths}
            onPinnedChange={(left, right) => setPinned(left, right)}
            onResetColumnOrder={resetColumnOrder}
            onResetColumnWidths={resetColumnWidths}
            loadingContent={
              <div className="flex flex-col items-center justify-center space-y-4 py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Loading transactions...</p>
              </div>
            }
            emptyContent={
              <div className="flex flex-col items-center justify-center gap-2 py-8">
                <Receipt className="h-12 w-12 text-muted-foreground/50" />
                <p className="text-base font-medium text-muted-foreground">
                  {getEmptyStateMessage(selectedMetricFilter)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {getEmptyStateDescription(selectedMetricFilter, searchQuery, !!hasActiveFilters)}
                </p>
                {(searchQuery || hasActiveFilters || selectedMetricFilter) && (
                  <Button variant="outline" size="sm" onClick={handleClearFilters} className="mt-2">
                    Clear All Filters
                  </Button>
                )}
              </div>
            }
            pagination={{
              currentPage,
              totalPages,
              pageSize,
              totalItems,
              onPageChange: setCurrentPage,
              onPageSizeChange: (size) => {
                setPageSize(size);
                setCurrentPage(1);
              },
              pageSizeOptions: [10, 25, 50, 100, 200],
            }}
          />
        </CardContent>
      </Card>

      {/* Filter Sheet */}
      <FilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        filters={filters}
        onApplyFilters={setFilters}
        savedPresets={savedPresets}
        activePresetId={effectiveActivePresetId}
        onApplyPreset={handleApplyPreset}
        onDeletePreset={handleDeletePreset}
        onSavePreset={(draft) => {
          pendingDraftFiltersRef.current = draft;
          setSavePresetDialogOpen(true);
        }}
        onClearAll={handleClearFilters}
      />

      {/* Detail Sheet */}
      <TransactionDetailSheet
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
        transaction={detailTransaction ?? null}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete transaction {transactionToDelete?.referenceNumber}. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Preset Dialog */}
      <Dialog open={savePresetDialogOpen} onOpenChange={setSavePresetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Filter Preset</DialogTitle>
            <DialogDescription>Save your current filters as a preset for quick access later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="preset-name">Preset Name</Label>
              <Input
                id="preset-name"
                placeholder="e.g., High Value Invoices"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePresetDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePreset}>
              <Save className="mr-2 h-4 w-4" />
              Save Preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Reminders Dialog */}
      <SendRemindersDialog
        open={sendRemindersDialogOpen}
        onOpenChange={setSendRemindersDialogOpen}
        transactions={selectedTransactions}
        onSend={handleSendReminders}
      />

      {/* Send Copy Invoice Dialog */}
      {selectedTransactions.length > 0 && (
        <TransactionSendCopyInvoiceModal
          open={sendCopyInvoiceDialogOpen}
          onOpenChange={setSendCopyInvoiceDialogOpen}
          transactions={selectedTransactions}
          onSuccess={handleSendCopyInvoiceSuccess}
        />
      )}

      {/* Create Collection Case Drawer */}
      <CreateCaseDrawer
        open={createCollectionCaseModalOpen}
        onOpenChange={setCreateCollectionCaseModalOpen}
        onCaseCreated={handleCreateCollectionCase}
        selectedTransactions={selectedTransactions}
        triggerSource="transactions-page"
      />

      {/* Create Promise to Pay Drawer */}
      <CreatePromiseDrawer
        open={createPromiseModalOpen}
        onOpenChange={setCreatePromiseModalOpen}
        prePopulatedCustomer={
          selectedTransactions.length > 0 && selectedTransactions[0]
            ? {
                customerId: selectedTransactions[0].customerId.toString(),
                customerName: selectedTransactions[0].customerName || '',
                accountNumber: selectedTransactions[0].accountNumber || '',
              }
            : undefined
        }
        prePopulatedTransactions={
          selectedTransactions.length > 0
            ? selectedTransactions.map((t) => ({
                id: t.id.toString(),
                headerReference: t.headerReference || t.referenceNumber,
                invoiceDate: new Date(t.transactionDate),
                originalAmount: t.amount,
                balanceRemaining: t.displayOutstandingAmount ?? t.outstandingAmount ?? t.amount,
                daysOverdue: t.dueDate
                  ? Math.max(
                      0,
                      Math.ceil((new Date().getTime() - new Date(t.dueDate).getTime()) / (1000 * 60 * 60 * 24)),
                    )
                  : 0,
              }))
            : undefined
        }
        triggerSource="action-bar"
        onSave={handleCreatePromise}
      />

      {/* Create Dispute Modal */}
      <CreateDisputeModal
        open={createDisputeModalOpen}
        onOpenChange={setCreateDisputeModalOpen}
        selectedTransactions={selectedTransactions}
        onSave={handleCreateDispute}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        open={addNoteModalOpen}
        onOpenChange={setAddNoteModalOpen}
        selectedTransactions={selectedTransactions}
        onSave={handleAddNote}
      />

      {/* Allocation History Modal */}
      <AllocationHistoryModal
        open={allocationHistoryModalOpen}
        onOpenChange={setAllocationHistoryModalOpen}
        transactionId={selectedTransactions.length > 0 ? selectedTransactions[0].id : null}
      />

      {/* Selection Toolbar - Floating at bottom when transactions are selected */}
      {selectedRows.size > 0 && selectedTransactions.length > 0 && selectionTotals && (
        <ErrorBoundary>
          <SelectionToolbar
            selectedCount={selectedRows.size}
            totals={selectionTotals}
            selectedTransactions={selectedTransactions}
            baseCurrency={baseCurrency}
            onClearSelection={() => setSelectedRows(new Set())}
            onExportCurrentView={handleExportCurrentView}
            onExportAllColumns={handleExportAllColumns}
            onSendReminders={() => setSendRemindersDialogOpen(true)}
            onAllocateReceipt={handleNavigateToAllocate}
            onSendCopyInvoice={() => setSendCopyInvoiceDialogOpen(true)}
            onCreateCollectionCase={() => setCreateCollectionCaseModalOpen(true)}
            onCreatePromise={() => setCreatePromiseModalOpen(true)}
            onCreateDispute={() => setCreateDisputeModalOpen(true)}
            onAddNote={() => setAddNoteModalOpen(true)}
            onViewAllocationHistory={handleViewAllocationHistory}
          />
        </ErrorBoundary>
      )}
    </div>
  );
}
